import { useCallback, useEffect, useRef, useState } from 'react'
import type {
  ConfidenceData,
  ConfidenceDay,
  DiskTrendData,
  EnvironmentInsight,
  FillingDisk,
} from '@netwatchai/chat-core'

const POLL_MS = 600_000 // 10 min

type Headers = Record<string, string> | undefined

// The shared Outlook owns the consumer view of these shapes; the hook seeds
// `data`/`history` from a dynamic JSON boundary and hands them back typed.
type ConfidenceHistory = ConfidenceDay[]

function cacheKey(headers: Headers): string {
  const cid = (headers && headers['X-Netwatch-Client-Id']) || 'default'
  return `netwatch:confidence:${cid}`
}
function readLocal(headers: Headers): ConfidenceData | null {
  try {
    const raw = localStorage.getItem(cacheKey(headers))
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}
function writeLocal(headers: Headers, data: ConfidenceData | null): void {
  try {
    localStorage.setItem(cacheKey(headers), JSON.stringify(data))
  } catch {
    /* storage unavailable */
  }
}

interface UseConfidenceOptions {
  apiEndpoint: string
  headers?: Record<string, string>
  enabled: boolean
}

/**
 * Whole-environment confidence for the Outlook tab + compact card.
 *
 * Stale-while-revalidate: seeds `data` from localStorage so the UI paints
 * instantly, then fetches `/confidence` (itself served from a shared 10-min
 * Redis cache server-side) and updates + re-caches. Polls every 10 min while
 * open; `refresh(true)` forces a server recompute (`?refresh=1`) — used when
 * the user opens the Outlook tab.
 */
export function useConfidence({ apiEndpoint, headers, enabled }: UseConfidenceOptions) {
  const [data, setData] = useState<ConfidenceData | null>(() => readLocal(headers))
  const [history, setHistory] = useState<ConfidenceHistory>([])
  // null = not loaded yet (tile shows "checking…"); [] = nothing filling.
  const [disks, setDisks] = useState<FillingDisk[] | null>(null)
  // null = not loaded yet; [] = none mined (tile falls back to the one-liner).
  const [insights, setInsights] = useState<EnvironmentInsight[] | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const headersRef = useRef(headers)
  headersRef.current = headers

  const refresh = useCallback(
    async (force = false) => {
      setLoading(true)
      setError(null)
      try {
        const q = force ? '?refresh=1' : ''
        const [scoreRes, histRes, disksRes, insRes] = await Promise.all([
          fetch(`${apiEndpoint}/confidence${q}`, { headers: headersRef.current }),
          fetch(`${apiEndpoint}/confidence/history?days=7`, { headers: headersRef.current }),
          fetch(`${apiEndpoint}/confidence/disks`, { headers: headersRef.current }),
          fetch(`${apiEndpoint}/confidence/insights`, { headers: headersRef.current }),
        ])
        if (!scoreRes.ok) throw new Error(`HTTP ${scoreRes.status}`)
        const fresh = await scoreRes.json()
        setData(fresh)
        writeLocal(headersRef.current, fresh)
        setHistory(histRes.ok ? await histRes.json() : [])
        // Disk feed is best-effort — a failure leaves the tile in its calm
        // (empty) state rather than erroring the whole Outlook.
        setDisks(disksRes.ok ? await disksRes.json() : [])
        // Insights are best-effort too — the tile degrades to the posture
        // one-liner when the endpoint is unavailable.
        setInsights(insRes.ok ? await insRes.json() : [])
      } catch (err) {
        setError((err as Error).message || 'Failed to load confidence')
      } finally {
        setLoading(false)
      }
    },
    [apiEndpoint],
  )

  useEffect(() => {
    if (!enabled) return
    refresh()
    const id = setInterval(refresh, POLL_MS)
    return () => clearInterval(id)
  }, [enabled, refresh])

  const getToday = useCallback(async () => {
    const res = await fetch(`${apiEndpoint}/confidence/today`, { headers: headersRef.current })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    return await res.json()
  }, [apiEndpoint])

  // One compiled past day for the 7-day strip's drill-down modal.
  const getDay = useCallback(async (isoDay: string) => {
    const res = await fetch(`${apiEndpoint}/confidence/day/${isoDay}`, { headers: headersRef.current })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    return await res.json()
  }, [apiEndpoint])

  const getProblems = useCallback(async () => {
    const res = await fetch(`${apiEndpoint}/confidence/problems`, { headers: headersRef.current })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    return await res.json()
  }, [apiEndpoint])

  // Per-drive usage trend + fill-up projection (the disk "Trend" chart). On
  // demand — fetched when a card is expanded / its zoom changes, not polled.
  const getDiskTrend = useCallback(async (host: string, drive: string, days: number): Promise<DiskTrendData> => {
    const q = `host=${encodeURIComponent(host)}&drive=${encodeURIComponent(drive)}&days=${days}`
    const res = await fetch(`${apiEndpoint}/confidence/disks/trend?${q}`, { headers: headersRef.current })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    return await res.json()
  }, [apiEndpoint])

  return { data, history, disks, insights, loading, error, refresh, getToday, getDay, getProblems, getDiskTrend }
}
